<script setup lang="ts">
import { computed } from 'vue'
import type { IStaffEmployee } from '../types/IStaffEmployee'
import { getRandomAvatar } from '@/utils/getRandomAvatar'

interface Props {
  departmentId: string | null
  departmentName: string
  employees: IStaffEmployee[]
  managerInfo?: IStructureManager | null
}

type IStructureManager = {
  id: string
  first_name: string
  last_name: string
  middle_name?: string
  position_name?: string
  avatar?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
  'select-employee': [employeeId: string]
}>()

// Сотрудники отделения (исключаем уволенных для чистоты отображения структуры)
const deptEmployees = computed(() => {
  return props.employees.filter(e => e.status !== 'Уволен')
})

const totalCount = computed(() => deptEmployees.value.length)

// Руководитель приходит вместе с подразделением из дерева отделений
const manager = computed(() => props.managerInfo || null)

// Подчиненные (все остальные сотрудники отделения, кроме руководителя)
const subordinates = computed(() => {
  const m = manager.value
  return deptEmployees.value.filter(e => !m || e.id !== m.id)
})

function getInitials(emp: IStaffEmployee | IStructureManager): string {
  const first = emp.first_name ? emp.first_name.charAt(0) : ''
  const last = emp.last_name ? emp.last_name.charAt(0) : ''
  return `${last}${first}`.toUpperCase()
}

const fallbackAvatars = new Map<string, string>()

function getAvatarUrl(emp: IStaffEmployee | IStructureManager): string {
  if (emp.avatar) return emp.avatar

  const id = String(emp.id)
  if (!fallbackAvatars.has(id))
    fallbackAvatars.set(id, getRandomAvatar())

  return fallbackAvatars.get(id)!
}
</script>

<template>
  <div class="d-flex flex-column h-100 w-100">
    <!-- Header -->
    <div class="d-flex align-center justify-space-between px-6 pt-6 pb-4">
      <div class="text-h5 pr-2 font-weight-medium flex-grow-1" style="line-height: 1.3;">
        {{ departmentName || 'Наименование МО' }}
      </div>
      <VTooltip
        location="bottom"
        text="Скрыть"
      >
        <template #activator="{ props: tooltipProps }">
          <VIconBtn
            v-bind="tooltipProps"
            size="small"
            variant="text"
            @click="emit('close')"
          >
            <VIcon icon="ri-play-fill" size="24" class="text-medium-emphasis" />
          </VIconBtn>
        </template>
      </VTooltip>
    </div>

    <!-- Content -->
    <div class="px-6 pb-6 overflow-y-auto flex-grow-1">
      <!-- Всего сотрудников -->
      <div class="d-flex align-center gap-2 mb-6 font-weight-medium" style="font-size: 15px; color: rgb(var(--v-theme-primary)) !important;">
        <VIcon icon="ri-team-line" size="20" style="color: rgb(var(--v-theme-primary)) !important;" />
        <span>Всего сотрудников {{ totalCount }}</span>
      </div>

      <!-- Секция: Руководитель -->
      <div class="mb-6">
        <div class="d-flex align-center mb-3">
          <span class="text-uppercase text-caption text-medium-emphasis" style="letter-spacing: 0.8px; opacity: 0.8;">
            Руководитель
          </span>
        </div>
        <div
          v-if="manager"
          class="d-flex align-center gap-3 employee-item py-2"
          @click="emit('select-employee', manager.id)"
        >
          <VAvatar size="40" variant="tonal">
            <VImg v-if="getAvatarUrl(manager)" :src="getAvatarUrl(manager)" />
            <span v-else>{{ getInitials(manager) }}</span>
          </VAvatar>
          <div class="flex-grow-1 min-width-0">
            <div class="text-body-1 text-truncate text-high-emphasis">
              {{ manager.last_name }} {{ manager.first_name }} {{ manager.middle_name || '' }}
            </div>
            <div class="text-caption text-medium-emphasis mt-0.5">
              {{ manager.position_name || 'Руководитель подразделения' }}
            </div>
          </div>
        </div>
        <div v-else class="text-caption text-error font-weight-medium py-1">
          Не назначен
        </div>
      </div>

      <!-- Секция: Подчиненные -->
      <div class="mb-6">
        <div class="d-flex align-center justify-space-between mb-3">
          <span class="text-uppercase text-caption text-medium-emphasis" style="letter-spacing: 0.8px; opacity: 0.8;">
            Подчинённые
          </span>
          <span class="count-chip rounded-circle d-inline-flex align-center justify-center text-caption font-weight-medium px-2" style="min-width: 24px; height: 24px; color: rgba(var(--v-theme-on-surface), 0.6); background-color: rgba(var(--v-theme-on-surface), 0.08) !important;">
            {{ subordinates.length }}
          </span>
        </div>
        <div v-if="subordinates.length === 0" class="text-body-2 opacity-60 text-center py-4">
          Нет сотрудников
        </div>
        <div v-else class="d-flex flex-column">
          <div
            v-for="emp in subordinates"
            :key="emp.id"
            class="d-flex align-center gap-3 employee-item py-2"
            @click="emit('select-employee', emp.id)"
          >
            <VAvatar size="40" variant="tonal">
              <VImg v-if="getAvatarUrl(emp)" :src="getAvatarUrl(emp)" />
              <span v-else>{{ getInitials(emp) }}</span>
            </VAvatar>
            <div class="flex-grow-1 min-width-0">
              <div class="text-body-1 text-truncate text-high-emphasis">
                {{ emp.last_name }} {{ emp.first_name }} {{ emp.middle_name || '' }}
              </div>
              <div class="text-caption text-medium-emphasis mt-0.5">
                {{ emp.position_name || emp.position?.name || 'Сотрудник' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.employee-item {
  padding-left: 0 !important;
  padding-right: 0 !important;
  cursor: pointer;
}
</style>
